/**
 * InviteButton — "Join my game" / "Let's match" on the card + connections list
 * (Phase D, AC-19/AC-20). Only meaningful for an accepted connection.
 */

import { useState } from 'react';
import { useActivity } from '../../context/ActivityContext';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import type { InviteType } from '../../types/social';

interface Props {
  toProfileId: string;
  type: InviteType;
  /** Required for a game_join invite — the room to invite them into. */
  roomId?: string;
  label?: string;
}

export function InviteButton({ toProfileId, type, roomId, label }: Props) {
  const enabled = useFeatureFlag('social.invites_feed');
  const { sendInvite } = useActivity();
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');

  if (!enabled) return null;
  // A game_join with no room to point at is not actionable.
  if (type === 'game_join' && !roomId) return null;

  const send = async () => {
    setState('sending');
    try {
      await sendInvite({ type, toProfileId, roomId });
      setState('sent');
    } catch {
      setState('idle');
    }
  };

  const text =
    state === 'sent'
      ? 'Invite sent'
      : label ?? (type === 'game_join' ? 'Join my game' : "Let's match");

  return (
    <button
      className="invite-button"
      disabled={state !== 'idle'}
      onClick={send}
    >
      {text}
    </button>
  );
}

export default InviteButton;
